import React, { useState, useEffect } from 'react'
import { Row, Col, Form, Modal, Input, Button, Select, Upload } from 'antd'
import '../styles/modal-styles.scss'
import closeIcon from 'assets/Images/close.svg'
import { useCreateClassesLateFee } from 'api/Classes/Step6Tickets/Tickets'
import { ClassesLateFee, PriceUnit } from 'types/Classes/Classes'
import { classesEventStore } from 'state/classes/classesStore'
import ButtonSpinner from 'components/global/ButtonSpinner'
import UploadDocument from 'components/global/UploadDocument'
import TicketColors from '../TicketColors'

interface LateFeeModalProps {
  modalOpen: boolean
  modalClose: React.Dispatch<React.SetStateAction<boolean>>
  data?: ClassesLateFee
}

const LateFee: React.FC<LateFeeModalProps> = ({
  modalOpen,
  modalClose,
  data,
}) => {
  const [form] = Form.useForm()
  const classesData = classesEventStore(store => store.classesData) as any
  const setClassesData = classesEventStore(store => store.setClassesData)
  const [ticketColor, setTicketColor] = useState<string>('#000000')
  const [documentIds, setDocumentIds] = useState<string[]>([])

  const { mutate: createLateFee, isLoading: createLateFeeLoading } = useCreateClassesLateFee()

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        title: data.title,
        price: data.price,
        description: data.description,
        priceUnit: data.priceUnit || PriceUnit.day,
      })
      setTicketColor(data.color || '#000000')
      if (data.imageId) {
        setDocumentIds([data.imageId])
      }
    } else {
      form.resetFields()
      setTicketColor('#000000')
      setDocumentIds([])
    }
  }, [data, form])

  const handleSubmit = (values: any) => {
    if (!classesData?._id) {
      console.error('Class ID not found')
      return
    }

    const payload: ClassesLateFee = {
      title: values.title,
      price: values.price,
      description: values.description,
      priceUnit: values.priceUnit || PriceUnit.day,
      color: ticketColor,
      imageId: documentIds[0] || undefined,
    }

    createLateFee(
      { payload, classId: classesData._id },
      {
        onSuccess: (result) => {
          setClassesData({ ...classesData, ...result })
          modalClose(false)
          form.resetFields()
          setDocumentIds([])
        },
      }
    )
  }

  const handleCancel = () => {
    modalClose(false)
    form.resetFields()
    setDocumentIds([])
  }

  return (
    <div>
      <Modal
        title={null}
        visible={modalOpen}
        onCancel={handleCancel}
        footer={null}
        width={600}
        closeIcon={<img src={closeIcon} alt="close" className="close-icon" />}
        className="custom-modal"
      >
        <div className="modal-content">
          <div className="flex-center pt-20px justify-between ">
            <h2 className="w-600 text-18">{data ? 'Edit Late Fee' : 'Add Late Fee'}</h2>
            <img
              src={closeIcon}
              alt="close-button"
              onClick={() => modalClose(false)}
              className="close button pointer"
            />
          </div>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            className="admission-ticket-form"
          >
            <Row gutter={16}>
              <Col span={24}>
                <label htmlFor="" className="custom-field-label required-input mt-20px">
                  Title
                </label>
                <Form.Item
                  name="title"
                  rules={[{ required: true, message: 'Please enter title' }]}
                >
                  <Input
                    className="form-control-field"
                    placeholder="Enter title"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <label htmlFor="" className="custom-field-label required-input">
                  Price
                </label>
                <Form.Item
                  name="price"
                  rules={[{ required: true, message: 'Please enter price' }]}
                >
                  <Input
                    type="number"
                    className="form-control-field"
                    placeholder="Enter price"
                    prefix="$"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <label htmlFor="" className="custom-field-label required-input">
                  Price Unit
                </label>
                <Form.Item
                  name="priceUnit"
                  rules={[{ required: true, message: 'Please select price unit' }]}
                  initialValue={PriceUnit.day}
                >
                  <Select className="form-control-field">
                    <Select.Option value={PriceUnit.day}>per day</Select.Option>
                    <Select.Option value={PriceUnit.hour}>per hour</Select.Option>
                    <Select.Option value={PriceUnit.minute}>per minute</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row>
              <Col span={24}>
                <label htmlFor="" className="custom-field-label">
                  Description
                </label>
                <Form.Item name="description">
                  <Input.TextArea
                    className="note-field"
                    placeholder="Terms of usage of the ticket"
                    autoSize={{ minRows: 3, maxRows: 10 }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row>
              <Col span={24}>
                <label className="input-label required-input">Ticket color</label>
                <TicketColors ticketColor={ticketColor} setTicketColor={setTicketColor} />
              </Col>
            </Row>

            <div className="w-80p mt-20px">
              <label className="input-label">Ticket image</label>
              <UploadDocument
                documentIds={documentIds}
                setDocumentIds={setDocumentIds}
                multiple={false}
                docType="image/*"
              />
            </div>

            <Row justify="end" className="mt-20px">
              <Button
                className="stepper-button"
                disabled={createLateFeeLoading}
                htmlType="submit"
              >
                {createLateFeeLoading ? (
                  <ButtonSpinner size={20} color="#000" />
                ) : (
                  'Save'
                )}
              </Button>
            </Row>
          </Form>
        </div>
      </Modal>
    </div>
  )
}

export default LateFee
